import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSiteContent } from "../lib/siteContent";

export function Header({ right, marketing = false }) {
  const { content } = useSiteContent();
  const [open, setOpen] = useState(false);
  const links = marketing ? content?.nav?.links || [] : [];

  return (
    <header
      className="sticky top-0 z-40 bg-[#FAF8F2]/95 backdrop-blur border-b border-[#E7E3D7]"
      data-testid="site-header"
    >
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 h-[68px] md:h-[76px] flex items-center justify-between gap-5">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-link">
          <img src="/brand/apt-mark.png" alt="APT" className="h-7 sm:h-8 w-auto" />
          <span className="hidden sm:inline-block text-[10px] font-semibold uppercase tracking-[0.28em] text-[#6B7A70]">
            bruddatrain
          </span>
        </Link>

        {links.length > 0 && (
          <nav className="hidden lg:flex items-center gap-8" data-testid="site-nav">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4A5A50] hover:text-[#0F6B3B] transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2 sm:gap-4">
          {right}
          {links.length > 0 && (
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden h-10 w-10 grid place-items-center rounded-md border border-[#E0DCCE] text-[#2E3B33]"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-[#E7E3D7] bg-[#FAF8F2] px-4 py-4"
          data-testid="mobile-menu"
        >
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#2E3B33] border-b border-[#EFEBDF] last:border-0"
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { content } = useSiteContent();
  const f = content?.footer || {};

  return (
    <footer className="mt-auto bg-[#0A4A2B] text-white" data-testid="site-footer">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-6">
          <img src="/brand/apt-mark.png" alt="APT" className="h-8 w-auto logo-on-dark" />
          <div className="font-display mt-5 text-xl sm:text-2xl font-semibold tracking-tight">
            {f.brand}
          </div>
          <p className="mt-2 max-w-sm text-sm text-white/55">{f.tagline}</p>
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#CDE05F]">
            {f.support_label}
          </div>
          {f.support_email && (
            <a
              href={`mailto:${f.support_email}`}
              className="mt-4 block text-sm text-white/85 hover:text-white"
              data-testid="footer-support-email"
            >
              {f.support_email}
            </a>
          )}
          <div className="mt-3 space-y-2">
            {(f.lines || []).map((l) => (
              <div key={l} className="text-sm text-white/50">
                {l}
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#CDE05F]">
            Access
          </div>
          <div className="mt-4 space-y-2.5 text-sm">
            <Link
              to="/subscribe"
              className="block text-white/85 hover:text-white"
              data-testid="footer-subscribe-link"
            >
              Subscribe
            </Link>
            <Link
              to="/auth"
              className="block text-white/85 hover:text-white"
              data-testid="footer-code-link"
            >
              Client code portal
            </Link>
            <Link
              to="/admin"
              className="block text-white/50 hover:text-white"
              data-testid="footer-admin-link"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-white/40">
          <div>
            © {new Date().getFullYear()} {f.copyright}
          </div>
          <div>apttrains.com</div>
        </div>
      </div>
    </footer>
  );
}
